import OrderItem from '~/models/order';
import type { OrderAdditionalDataInterface } from '~/constants/interfaces/order-additional-data';
import { useConfigStore } from '~/store/config';
import { pushToDataLayer } from '~/composables/useTracking';
import { useCartStore } from '~/store/cart';
import type { OrderItemType } from '~/constants/types/order-item';
import useApiFetch from '~/composables/useApiFetch';

export type PaymentResultResponse = {
  token: string,
  order: OrderItemType,
}

export default function useOrderComplete() {
  const orderAdditionalData = useCookie<OrderAdditionalDataInterface | null>('orderAdditionalData', { maxAge: 3600 * 24 * 182, path: '/' });
  const { unifaunPost, apiPost } = useApiFetch();
  const runtimeConfig = useRuntimeConfig();
  const config = useConfigStore();
  const cartStore = useCartStore();

  /**
   * After order is placed, this creates the unifaun shipping
   */
  const createPreparedShipment = async(order: OrderItem) => {
    if (orderAdditionalData.value) {
      console.log('createPreparedShipment');
      console.log(orderAdditionalData.value);

      const postData = {
        shipment: {
          receiver: {
            name: `${order.shippingAddress.firstName} ${order.shippingAddress.lastName}`,
            address1: `${order.shippingAddress.address1}`,
            zipcode: `${order.shippingAddress.zipCode}`,
            city: `${order.shippingAddress.city}`,
            country: `${order.shippingAddress.country}`,
            phone: `${order.shippingAddress.phoneNumber}`,
            email: `${order.shippingAddress.email}`,
          },
          agent: {} as undefined | object,
          orderNo: order.orderId,
          senderReference: orderAdditionalData.value.preparedId,
        },
        selectedOptionId: orderAdditionalData.value.shippingOptions?.selectedOptionId,
        selectedAddons: orderAdditionalData.value?.shippingOptions?.selectedAddons ?? [],
        returnShipmentData: true,
        prepareId: orderAdditionalData.value.preparedId,
        language: config.market.language,
      };
      if (orderAdditionalData.value?.shippingOptions?.selectedAgent) {
        postData.shipment.agent = {
          quickId: orderAdditionalData.value?.shippingOptions?.selectedAgent,
        };
      } else {
        delete postData.shipment.agent;
      }
      // Fix for empty phone number
      const notifySmsAddon = postData.selectedAddons?.find(
        (a) => a.id === 'FCNOTIFYSMS'
      );
      if (
        notifySmsAddon &&
        (notifySmsAddon.fields?.length === 0 ||
          notifySmsAddon.fields?.find((f) => f.value?.length === 0))
      ) {
        if (order?.shippingAddress?.phoneNumber) {
          postData.selectedAddons = [
            {
              id: 'FCNOTIFYSMS',
              price: 0,
              fields: [
                {
                  id: 'sms',
                  value: order?.shippingAddress?.phoneNumber,
                },
              ],
            },
          ];
        }
      }
      // Fix for Bring in finland which needs this selectedAddon (not documented in unifaun anywhere)
      if (
        orderAdditionalData.value?.shippingOptions?.selectedOptionId ===
        '4892c80f-3b88-41e6-9e9f-deb13e5d3fc2'
      ) {
        postData.selectedAddons.push({ id: 'PUPOPT' });
      }
      let res = {} as any;
      console.log('postdata: '+JSON.stringify(postData));
      try {
        res = await unifaunPost(
          `/api/unifaun/delivery-checkouts/${runtimeConfig.public.unifaunId}`,
          postData
        );

        console.log('res: '+JSON.stringify(res));

      } catch (e: any) {
        console.error(e);
        res.data = {
          response: e.response,
          request: e.request,
          message: e.message,
          config: e.config,
        };
      }

      try {
        const shipmentData = Object.assign(
          { response: res.data },
          { request: postData }
        );
        await apiPost('/wp-json/api/v1/orders', {
          prepared_shipment_id: orderAdditionalData.value?.preparedId,
          prepared_shipment_data: shipmentData,
        });
      } catch (e: any) {
        console.error(e);
      }

      /**
       * Delete cookie
       * For debugging just comment this row and it will send on every reload on thank you page
       */
      orderAdditionalData.value = null;
    } else {
      console.log('No data to send to Unifaun');
    }
  };

  /**
   * Tracking
   */
  const sendGtmReceipt = (order: OrderItem) => {
    if (order) {
      const items = order.items.map((item) => {
        return {
          id: item.sku,
          feedId: item.sku,
          ...item.product.gtmProductValues,
          quantity: item.quantity,
        };
      });

      const payload = {
        event: 'purchase',
        email: order.order?.shippingAddress?.email,
        ecommerce: {
          currencyCode: cartStore.cartData.currency,
          purchase: {
            actionField: {
              id: order.orderId,
              revenue: order.totalPrice.replace(/\s/g, ''),
              tax: order.taxAddedAsNumber.toString().replace(/\s/g, ''),
              shipping: order.shippingPriceAsNumber
                .toString()
                .replace(/\s/g, ''),
              coupon: order.voucher,
            },
            products: items,
          },
        },
      };
      console.log(payload);
      pushToDataLayer(payload);
    } else {
      console.warn('No order');
    }
  };

  const setNewsletter = () => {
    const hasAcceptedNewsletter =
      sessionStorage.getItem('acceptedNewsletter');

    const newsletterIsAccepted = hasAcceptedNewsletter
      ? JSON.parse(hasAcceptedNewsletter)
      : '';

    if (newsletterIsAccepted) {
      pushToDataLayer({
        event: 'email_opt_in',
        location: 'checkout',
      });
    }
  };

  const setClubSellerText = async(orderId: string, sellerText: string) => {
    const res = await $fetch(`/api/updateOrder/?id=${orderId}&textstr=${sellerText}`);
    console.log('Setting thank you-text on order: ');
    //console.log(res.message);
    console.log(JSON.stringify(res));
  };

  return {
    createPreparedShipment,
    sendGtmReceipt,
    setNewsletter,
    setClubSellerText,
  };

}
